import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import { 
  FaWindows, 
  FaApple, 
  FaDownload, 
  FaTools, 
  FaUsb, 
  FaFilm,
  FaArrowRight
} from 'react-icons/fa'

// 精简后的指南卡片，只保留6个
const guides = [
  {
    id: 'windows',
    title: "Windows重装指南",
    desc: "一步步引导你完成Windows系统的U盘安装，从备份到激活，一条龙服务。",
    icon: <FaWindows />,
    iconColor: "text-blue-500",
    bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
    iconBgColor: "bg-blue-100",
    borderColor: "border-blue-200",
    hoverColor: "hover:bg-blue-100",
  },
  {
    id: 'mac',
    title: "Mac重装指南",
    desc: "教你如何重装macOS，制作启动盘，恢复出厂系统，安全备份不迷路。",
    icon: <FaApple />,
    iconColor: "text-red-500",
    bgColor: "bg-gradient-to-br from-red-50 to-red-100",
    iconBgColor: "bg-red-100",
    borderColor: "border-red-200",
    hoverColor: "hover:bg-red-100",
  },
  {
    id: 'downloads',
    title: "系统下载资源",
    desc: "精选Windows和Mac系统下载站点，包含原版、纯净、精简版本，拒绝捆绑软件。",
    icon: <FaDownload />,
    iconColor: "text-green-500",
    bgColor: "bg-gradient-to-br from-green-50 to-green-100",
    iconBgColor: "bg-green-100",
    borderColor: "border-green-200",
    hoverColor: "hover:bg-green-100",
  },
  {
    id: 'tools',
    title: "必备工具推荐",
    desc: "精选装机工具，如Ventoy、Rufus、NTLite、U盘启动工具等，满足你的装机需求。",
    icon: <FaTools />,
    iconColor: "text-purple-500",
    bgColor: "bg-gradient-to-br from-purple-50 to-purple-100",
    iconBgColor: "bg-purple-100",
    borderColor: "border-purple-200",
    hoverColor: "hover:bg-purple-100",
  },
  {
    id: 'usb',
    title: "U盘选购指南",
    desc: "推荐靠谱好用的装机U盘品牌和型号，读写速度测评与性价比分析。",
    icon: <FaUsb />,
    iconColor: "text-cyan-500",
    bgColor: "bg-gradient-to-br from-cyan-50 to-cyan-100",
    iconBgColor: "bg-cyan-100",
    borderColor: "border-cyan-200",
    hoverColor: "hover:bg-cyan-100",
  },
  {
    id: 'videos',
    title: "视频教学合集",
    desc: "精选各类高质量视频教程，配合文字指引，事半功倍，轻松学会装机技巧。",
    icon: <FaFilm />,
    iconColor: "text-pink-500",
    bgColor: "bg-gradient-to-br from-pink-50 to-pink-100",
    iconBgColor: "bg-pink-100",
    borderColor: "border-pink-200",
    hoverColor: "hover:bg-pink-100",
  },
]

// 封装卡片组件，使布局更加一致
const GuideCard = ({ guide, to }) => {
  return (
    <div className={`h-full ${guide.bgColor} rounded-lg shadow-md hover:shadow-lg border ${guide.borderColor} transition-all duration-300 transform hover:-translate-y-1 overflow-hidden`}>
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className={`text-2xl p-3 rounded-full mr-4 ${guide.iconColor} ${guide.iconBgColor}`}>{guide.icon}</div>
          <h3 className="text-xl font-bold text-gray-800">{guide.title}</h3>
        </div>
        <p className="text-gray-600 leading-relaxed mb-4">{guide.desc}</p>
      </div>
      <div className={`px-6 py-3 flex justify-center items-center border-t ${guide.borderColor} ${guide.hoverColor} transition-colors duration-300`}>
        <span className="text-sm font-medium text-gray-600 mr-1">阅读更多</span>
        <FaArrowRight className="text-xs text-gray-500" />
      </div>
    </div>
  );
};

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="装个机 - 专业电脑重装系统指南教程 | 一键重装系统 | Windows/Mac系统安装"
        description="专业为普通用户打造的电脑重装系统指南教程网站，提供Windows重装系统教程、Mac OS重装系统指南、一键重装系统工具、U盘启动盘制作方法，让系统重装变得简单快捷。"
        keywords="装机,重装系统,一键重装系统,Windows重装系统,Mac重装系统,系统安装教程,U盘启动,装机教程,系统重装教程,重装电脑系统指南"
      />
      <header className="mb-16 text-center p-8">
        <motion.h2 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl sm:text-5xl font-bold mb-3 text-primary"
        >
          装个机 ZhuangIt
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-xl sm:text-2xl text-gray-600 font-medium"
        >
          不求人，自己装 💻🧰
        </motion.p>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {guides.map((guide, index) => (
            <motion.div
              key={guide.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex"
            >
              <Link 
                to={
                  guide.id === 'downloads' ? '/downloads' : 
                  guide.id === 'videos' ? '/videos' : 
                  guide.id === 'tools' ? '/tools' :
                  guide.id === 'usb' ? '/usb-recommend' :
                  `/guide/${guide.id}`
                }
                className="w-full"
              >
                <GuideCard guide={guide} to="" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  )
}

export default Home 